import { groupBy, intersection, isEmpty, mapValues } from "lodash";
import type { SelectedTag } from "../../components/tagSelect/TagSelect";
import type { AdminCustomerListItem, CustomerStatus } from "../customersApi";
import type { CustomerFilters } from "./CustomerSearch";

type Customer = Pick<
  AdminCustomerListItem,
  "name" | "publicContact" | "contacts" | "tags" | "contactDate" | "status" | "accountManager" | "travelPay" | "taxNo"
>;

export function filterCustomers<T extends Customer>(
  customers: T[],
  { query, statuses, travelPayStatus, accountManagerIds, marketSegment }: CustomerFilters,
): T[] {
  return customers
    .filter(filterCustomerBySearchQuery(query.toLowerCase()))
    .filter(filterByStatus(statuses))
    .filter(filterByTravelPayStatus(travelPayStatus))
    .filter(filterByAccountManager(accountManagerIds))
    .filter(filterByMarketSegment(marketSegment));
}

function valueContainsSearchQuery(value: string | undefined, searchQuery: string): boolean {
  return value?.toLowerCase().includes(searchQuery) ?? false;
}

function filterCustomerBySearchQuery(
  searchQuery: string,
): Predicate<Pick<Customer, "name" | "publicContact" | "contacts" | "tags" | "taxNo">> {
  return (customer) =>
    isEmpty(searchQuery) ||
    valueContainsSearchQuery(customer.name, searchQuery) ||
    valueContainsSearchQuery(customer.publicContact?.city, searchQuery) ||
    valueContainsSearchQuery(customer.publicContact?.voivodeship, searchQuery) ||
    valueContainsSearchQuery(customer.taxNo, searchQuery) ||
    (customer.contacts ?? []).some((c) => valueContainsSearchQuery(c.email, searchQuery)) ||
    (customer.contacts ?? []).some((c) => valueContainsSearchQuery(c.phone, searchQuery));
}

function filterByMarketSegment(marketSegment: SelectedTag[]): Predicate<Pick<Customer, "tags">> {
  const tagsByCategory = groupTagsByCategory(marketSegment);
  return (c) => {
    if (marketSegment.length === 0) {
      return true;
    }
    const customerTagsByCategory = groupTagsByCategory(c.tags.marketSegment);
    return Object.entries(tagsByCategory).every(
      ([category, selectedTags]) => intersection(selectedTags, customerTagsByCategory[category] ?? []).length > 0,
    );
  };
}

function groupTagsByCategory(tags: SelectedTag[]): Record<string, string[]> {
  return mapValues(
    groupBy(tags, (t) => t.category),
    (tags) => tags.map((t) => t.value),
  );
}

function filterByAccountManager(agentIds: string[]): Predicate<Pick<Customer, "accountManager" | "travelPay">> {
  return (c) => {
    if (agentIds.length === 0) {
      return true;
    }
    const accountManagerId = c.accountManager?.id;
    const travelPayManagerId = c.travelPay?.accountManager?.id;
    return (
      (accountManagerId !== undefined && agentIds.includes(accountManagerId)) ||
      (travelPayManagerId !== undefined && agentIds.includes(travelPayManagerId))
    );
  };
}

function filterByStatus(statuses: CustomerStatus[]): Predicate<Pick<Customer, "status">> {
  return (c) => statuses.length === 0 || statuses.includes(c.status);
}

function filterByTravelPayStatus(status: CustomerStatus | undefined): Predicate<Pick<Customer, "travelPay">> {
  return (c) => status === undefined || c.travelPay?.status === status;
}
